'use client';

import { AlertTriangle, FileX, Lock, RefreshCw } from 'lucide-react';

import type { EstadoDocumentoComVoltarProps } from '../../types/encerramento.types';
import { classesDoTom } from '../../utils/tonsEncerramento';

export function DocumentoCarregando() {
  return (
    <div aria-busy="true" aria-label="Carregando documento" className="bg-neutral-50 py-6">
      <div className="mx-auto w-full max-w-[794px] space-y-4 px-4">
        <div className="h-8 w-full animate-pulse rounded-[8px] bg-neutral-100" />
        <div className="bg-default-background space-y-3 rounded-[16px] border border-solid border-neutral-200 p-8">
          <div className="h-10 w-56 animate-pulse rounded-md bg-neutral-100" />
          <div className="h-4 w-full animate-pulse rounded-md bg-neutral-100" />
          <div className="h-4 w-4/5 animate-pulse rounded-md bg-neutral-100" />
          <div className="h-20 w-full animate-pulse rounded-[8px] bg-neutral-100" />
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-neutral-100" />
        </div>
      </div>
    </div>
  );
}

export function DocumentoNaoEncontrado({ onVoltar }: EstadoDocumentoComVoltarProps) {
  return (
    <Centralizado>
      <FileX aria-hidden="true" className="h-7 w-7 text-neutral-500" />
      <p className="text-sm font-medium text-neutral-900">Documento não encontrado</p>
      <p className="max-w-sm text-xs text-neutral-600">
        Este documento pode não ter sido emitido ainda, ou o endereço está incorreto.
      </p>
      {onVoltar ? <AcaoDoEstado onClick={onVoltar}>Voltar ao processo</AcaoDoEstado> : null}
    </Centralizado>
  );
}

/**
 * Sentença ainda não proferida: não existe comprovação a exibir (rn8).
 *
 * Distinto de `DocumentoNaoEncontrado` de propósito — "documento não encontrado" sugere endereço
 * errado, e aqui o endereço está certo: o que falta é o ato. A pessoa precisa saber que não há
 * nada de errado com o link.
 */
export function SentencaNaoProferida({ onVoltar }: EstadoDocumentoComVoltarProps) {
  return (
    <Centralizado>
      <FileX aria-hidden="true" className="h-7 w-7 text-neutral-500" />
      <p className="text-sm font-medium text-neutral-900">Sentença ainda não proferida</p>
      <p className="max-w-sm text-xs text-neutral-600">
        A sentença deste processo ainda não foi publicada, então não há comprovação de comunicação a
        exibir.
      </p>
      {onVoltar ? <AcaoDoEstado onClick={onVoltar}>Voltar ao processo</AcaoDoEstado> : null}
    </Centralizado>
  );
}

export function DocumentoComErro({
  mensagem,
  onTentarNovamente,
}: {
  readonly mensagem: string;
  readonly onTentarNovamente?: () => void;
}) {
  const tomErro = classesDoTom('erro');
  return (
    <Centralizado>
      <AlertTriangle aria-hidden="true" className={`h-7 w-7 ${tomErro.texto}`} />
      <p className="text-sm font-medium text-neutral-900">
        Não conseguimos carregar este documento
      </p>
      <p className="max-w-sm text-xs text-neutral-600">{mensagem}</p>
      {onTentarNovamente ? (
        <AcaoDoEstado onClick={onTentarNovamente}>
          <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
          Tentar de novo
        </AcaoDoEstado>
      ) : null}
    </Centralizado>
  );
}

export function DocumentoSemPermissao() {
  return (
    <Centralizado>
      <Lock aria-hidden="true" className="h-7 w-7 text-neutral-500" />
      <p className="text-sm font-medium text-neutral-900">Acesso restrito</p>
      <p className="max-w-sm text-xs text-neutral-600">
        Você não tem permissão para ver este documento.
      </p>
    </Centralizado>
  );
}

/**
 * Acao secundaria dos estados vazios. Extraida porque a mesma classe estava copiada em TRES
 * botoes — `DocumentoNaoEncontrado`, `SentencaNaoProferida` e `DocumentoComErro`; a terceira
 * ocorrencia entrou com este PR. Um dono so tambem e o que permite trocar essas classes por
 * tokens do design system num lugar, em vez de em tres.
 */
function AcaoDoEstado({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) {
  return (
    <button
      className="flex items-center gap-1.5 rounded-[8px] border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700"
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function Centralizado({ children }: { readonly children: React.ReactNode }) {
  return (
    <div className="bg-neutral-50 py-6">
      <div className="bg-default-background mx-auto flex w-full max-w-[794px] flex-col items-center gap-2 rounded-[16px] border border-solid border-neutral-200 p-10 text-center">
        {children}
      </div>
    </div>
  );
}
